import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getVocabulary } from '@plone/volto/actions/vocabularies/vocabularies';
import {
  CaretDownIcon,
  CaretUpIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
} from '@phosphor-icons/react';
import {
  MPFButton,
  MPFDatePicker,
  MPFInput,
} from 'volto-ministerio-publico-federal/components/UI';
import {
  DEFAULT_NEWS_FILTERS,
  SEARCHABLE_TEXT_MAX_LENGTH,
  parseNewsFilters,
} from 'volto-ministerio-publico-federal/hooks/useNewsFilters';
import { toISODate } from 'volto-ministerio-publico-federal/utils/brDate';
import NewsCategoryPicker from './NewsCategoryPicker';
import NewsFilterSummary from './NewsFilterSummary';
import NewsContext from './NewsContext';

const VOCAB_NAME = 'ministerio.publico.federal.noticiatemas';
const UNIDADE_VOCAB_NAME = 'ministerio.publico.federal.unidades_origem';

const DATE_ERROR = 'A data final não pode ser anterior à inicial.';

const withCurrentValue = (options, value, valueKey, labelKey) => {
  if (!value || options.some((option) => option[valueKey] === value)) {
    return options;
  }

  return [...options, { [valueKey]: value, [labelKey]: value }];
};

const NewsForm = () => {
  const dispatch = useDispatch();
  const { items } = useSelector(
    (state) => state.vocabularies[VOCAB_NAME] || {},
  );
  const { items: unidadesItems } = useSelector(
    (state) => state.vocabularies[UNIDADE_VOCAB_NAME] || {},
  );
  const itemsTotal = useSelector(
    (state) => state.listNewsPortal?.result?.items_total,
  );
  const location = useLocation();
  const pathname = location.pathname;
  const { setParameters } = useContext(NewsContext);

  const [isOpen, setIsOpen] = useState(false);

  const currentFilters = useMemo(
    () => parseNewsFilters(location.search),
    [location.search],
  );

  const [fields, setFields] = useState(currentFilters);
  const [resetToken, setResetToken] = useState(0);

  useEffect(() => {
    setFields(currentFilters);
    setResetToken((current) => current + 1);
  }, [currentFilters]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFields((current) => ({ ...current, [name]: value }));
  };

  const handleDateChange = (name) => (value) => {
    setFields((current) => ({ ...current, [name]: value }));
  };

  const setSubject = (updateSubject) => {
    setFields((current) => ({
      ...current,
      subject: updateSubject(current.subject),
    }));
  };

  useEffect(() => {
    dispatch(getVocabulary({ vocabNameOrURL: VOCAB_NAME }));
    dispatch(getVocabulary({ vocabNameOrURL: UNIDADE_VOCAB_NAME }));
  }, [dispatch]);

  const todayISO = toISODate(new Date());
  const startMax =
    fields.date_end && fields.date_end < todayISO ? fields.date_end : todayISO;

  const dateError =
    fields.date_start && fields.date_end && fields.date_end < fields.date_start
      ? DATE_ERROR
      : '';

  const onSubmit = (event) => {
    event.preventDefault();

    if (dateError) {
      return;
    }

    setParameters(fields);
    setIsOpen(false);
  };

  const onClear = () => {
    setFields(DEFAULT_NEWS_FILTERS);
    setResetToken((current) => current + 1);
    setParameters(DEFAULT_NEWS_FILTERS);
  };

  const unidadeOptions = withCurrentValue(
    [{ token: '', title: 'Todas' }].concat(
      (unidadesItems || [])
        .filter((item) => item.value !== '__novalue__')
        .map((item) => ({ token: item.value, title: item.label })),
    ),
    fields.unidade,
    'token',
    'title',
  );

  const Caret = isOpen ? CaretUpIcon : CaretDownIcon;

  return (
    <div className="mpf-news-filter">
      {}
      <details
        className="mpf-news-filter__details"
        open={isOpen}
        onToggle={(event) => setIsOpen(event.currentTarget.open)}
      >
        <summary className="mpf-news-filter__summary-row">
          <FunnelIcon className="mpf-news-filter__icon" aria-hidden="true" />
          <span className="mpf-news-filter__title">Filtros e busca</span>
          <NewsFilterSummary
            filters={currentFilters}
            unidades={unidadesItems}
          />
          <Caret className="mpf-news-filter__icon" aria-hidden="true" />
        </summary>

        <form
          className="mpf-news-filter__form"
          action={pathname}
          onSubmit={onSubmit}
        >
          <div className="mpf-news-filter__fields">
            <div className="mpf-news-filter__row">
              <MPFInput
                label="Buscar"
                id="search"
                name="searchable_text"
                type="search"
                placeholder="Buscar por palavra-chave"
                maxLength={SEARCHABLE_TEXT_MAX_LENGTH}
                leadingIcon={<MagnifyingGlassIcon size={18} />}
                value={fields.searchable_text}
                onChange={handleChange}
              />

              <div className="mpf-form-control">
                <label className="mpf-label" htmlFor="unidade">
                  <span className="mpf-label-text">Unidades</span>
                </label>
                <div className="mpf-input mpf-news-filter__select">
                  <select
                    id="unidade"
                    name="unidade"
                    value={fields.unidade}
                    onChange={handleChange}
                  >
                    {unidadeOptions.map((unidade) => (
                      <option key={unidade.token} value={unidade.token}>
                        {unidade.title}
                      </option>
                    ))}
                  </select>
                  <CaretDownIcon
                    className="mpf-news-filter__select-caret"
                    size={18}
                    aria-hidden="true"
                  />
                </div>
              </div>
            </div>

            <div className="mpf-news-filter__row">
              <MPFDatePicker
                key={`date_start-${resetToken}`}
                label="Publicada entre"
                id="date_start"
                name="date_start"
                max={startMax}
                value={fields.date_start}
                onChange={handleDateChange('date_start')}
              />
              <MPFDatePicker
                key={`date_end-${resetToken}`}
                label="Até"
                id="date_end"
                name="date_end"
                min={fields.date_start || undefined}
                max={todayISO}
                value={fields.date_end}
                onChange={handleDateChange('date_end')}
                errorText={dateError || undefined}
              />
            </div>

            <NewsCategoryPicker
              items={items}
              selected={fields.subject}
              onChange={setSubject}
            />
          </div>

          <div className="mpf-news-filter__actions">
            <p className="mpf-news-filter__count" aria-live="polite">
              Mostrando {itemsTotal ?? 0} notícias
            </p>

            <MPFButton
              type="button"
              variant="ghost"
              size="m"
              className="mpf-news-filter__clear"
              onClick={onClear}
            >
              Limpar filtros
            </MPFButton>
            <MPFButton
              type="submit"
              variant="primary"
              size="m"
              className="mpf-news-filter__submit"
            >
              Buscar
            </MPFButton>
          </div>
        </form>
      </details>
    </div>
  );
};

export default NewsForm;
